import { Insertion, LayoutModel, ProjectLayoutModel } from '@paragon/contract-library';
import { ReadonlyRecord } from '@paragon/utilities';

import { LayoutModelsLibraryState } from '../store';
import { getInsertionsWithPendingChangesGroupedByObjectGuid, getLayoutModelsWithPendingChanges } from './changeTracker';
import { getParentGuidsFromInsertions } from './getParents';

export function getProjectAncestor(state: LayoutModelsLibraryState, modelGuid: string) {
  const layoutModels = getLayoutModelsWithPendingChanges(state);
  const insertions = getInsertionsWithPendingChangesGroupedByObjectGuid(state);

  return getProjectAncestorFromLayoutModelsAndInsertions(layoutModels, insertions, modelGuid);
}

export function getProjectAncestorFromLayoutModelsAndInsertions(
  layoutModels: ReadonlyRecord<string, LayoutModel | undefined>,
  insertionsGroupedByObjectGuid: ReadonlyRecord<string, readonly Insertion[] | undefined>,
  initialModelGuid: string,
) {
  let projectLayoutModel: ProjectLayoutModel | null = null;

  const modelGuidsToProcess = new Set([initialModelGuid]);
  let currentModelGuid = popValueFromSet(modelGuidsToProcess);
  while (currentModelGuid != null) {
    const model = layoutModels[currentModelGuid];
    if (model == null) {
      throw new Error(`Could not find model ${currentModelGuid}.`);
    } else if (model.type === 'project') {
      if (projectLayoutModel == null) {
        projectLayoutModel = model as ProjectLayoutModel;
      } else {
        if (projectLayoutModel.guid !== model.guid) {
          throw new Error(`Found multiple project ancestors for model ${initialModelGuid}.`);
        }
      }
    }

    const parentGuids = getParentGuidsFromInsertions(insertionsGroupedByObjectGuid, currentModelGuid);
    for (const parentGuid of parentGuids) {
      modelGuidsToProcess.add(parentGuid);
    }

    currentModelGuid = popValueFromSet(modelGuidsToProcess);
  }

  if (projectLayoutModel == null) {
    throw new Error(`Could not find project ancestor for model ${initialModelGuid}.`);
  }

  return projectLayoutModel;
}

function popValueFromSet(set: Set<string>): string | undefined {
  const value = set.values().next().value;
  if (value != null) set.delete(value);
  return value;
}
